/**
 * Slack Notifications — Slack incoming webhook configuration.
 *
 * Customer setup flow (shown in the UI):
 *   1. In Slack: go to api.slack.com/apps → create or open your Velaro app
 *      → Incoming Webhooks → toggle On → Add New Webhook to Workspace → pick channel → Copy URL
 *   2. Paste URL here, name it, pick trigger events, click Save
 *   3. Click Test to verify the message appears in Slack
 *   Velaro fires notifications automatically from then on.
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import IntegrationActivityLog from "./IntegrationActivityLog";

interface SlackConfig {
  id: number;
  name: string;
  webhookUrl: string;
  notifyOnResolve: boolean;
  notifyOnNewChat: boolean;
  notifyOnMissed: boolean;
  notifyOnSlaBreach: boolean;
  isEnabled: boolean;
  createdAt: string;
}

interface FormState {
  name: string;
  webhookUrl: string;
  notifyOnResolve: boolean;
  notifyOnNewChat: boolean;
  notifyOnMissed: boolean;
  notifyOnSlaBreach: boolean;
}

const EMPTY_FORM: FormState = {
  name: "",
  webhookUrl: "",
  notifyOnResolve: true,
  notifyOnNewChat: false,
  notifyOnMissed: false,
  notifyOnSlaBreach: false
};

export default function SlackNotifications() {
  const api = useApi();
  const [configs, setConfigs] = useState<SlackConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<SlackConfig | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [testing, setTesting] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<Record<number, { success: boolean; message: string }>>({});
  const [deleting, setDeleting] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.admin.get("SlackNotification");
      const data = await res.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSaveError("");
    setShowModal(true);
  }

  function openEdit(c: SlackConfig) {
    setEditing(c);
    setForm({
      name: c.name,
      webhookUrl: c.webhookUrl,
      notifyOnResolve: c.notifyOnResolve,
      notifyOnNewChat: c.notifyOnNewChat,
      notifyOnMissed: c.notifyOnMissed,
      notifyOnSlaBreach: c.notifyOnSlaBreach
    });
    setSaveError("");
    setShowModal(true);
  }

  async function save() {
    if (!form.webhookUrl.trim()) { setSaveError("Webhook URL is required."); return; }
    setSaving(true);
    setSaveError("");
    try {
      const body = {
        name: form.name || "Slack Channel",
        webhookUrl: form.webhookUrl,
        notifyOnResolve: form.notifyOnResolve,
        notifyOnNewChat: form.notifyOnNewChat,
        notifyOnMissed: form.notifyOnMissed,
        notifyOnSlaBreach: form.notifyOnSlaBreach,
        isEnabled: editing?.isEnabled ?? true
      };
      const res = editing
        ? await api.admin.put(`SlackNotification/${editing.id}`, body)
        : await api.admin.post("SlackNotification", body);
      const data = await res.json();
      if (!res.ok) { setSaveError(data?.error ?? "Save failed."); return; }
      setShowModal(false);
      await load();
    } catch {
      setSaveError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(c: SlackConfig) {
    try {
      await api.admin.put(`SlackNotification/${c.id}`, {
        name: c.name,
        webhookUrl: c.webhookUrl,
        notifyOnResolve: c.notifyOnResolve,
        notifyOnNewChat: c.notifyOnNewChat,
        notifyOnMissed: c.notifyOnMissed,
        notifyOnSlaBreach: c.notifyOnSlaBreach,
        isEnabled: !c.isEnabled
      });
      await load();
    } catch { /* ignore */ }
  }

  async function deleteConfig(id: number) {
    if (!confirm("Delete this Slack webhook configuration?")) return;
    setDeleting(id);
    try {
      await api.admin.delete(`SlackNotification/${id}`);
      await load();
    } catch { /* ignore */ } finally {
      setDeleting(null);
    }
  }

  async function test(id: number) {
    setTesting(id);
    setTestResults(prev => ({ ...prev, [id]: undefined as any }));
    try {
      const res = await api.admin.post(`SlackNotification/Test/${id}`, {});
      const data = await res.json();
      setTestResults(prev => ({ ...prev, [id]: data }));
    } catch {
      setTestResults(prev => ({ ...prev, [id]: { success: false, message: "Test request failed." } }));
    } finally {
      setTesting(null);
    }
  }

  const triggers = (c: SlackConfig) => [
    c.notifyOnResolve   && "Resolved",
    c.notifyOnNewChat   && "New chat",
    c.notifyOnMissed    && "Missed",
    c.notifyOnSlaBreach && "SLA breach"
  ].filter(Boolean).join(", ") || "None";

  return (
    <div className="p-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#4A154B]">
            {/* Slack hash mark */}
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
              <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Slack Notifications</h1>
            <p className="text-sm text-slate-500">
              Send alerts to Slack channels when conversations resolve, arrive, or breach SLA.
            </p>
          </div>
        </div>
        <PrimaryBlueButton label="Add Webhook" onClick={openCreate} />
      </div>

      {/* Setup instructions */}
      <SetupInstructions />

      {/* Webhook list */}
      {loading ? (
        <div className="text-sm text-slate-400 py-4">Loading...</div>
      ) : configs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
          <p className="text-sm font-medium text-slate-600 mb-1">No webhooks configured yet</p>
          <p className="text-xs text-slate-400 mb-4">Add a Slack incoming webhook to start receiving notifications.</p>
          <PrimaryBlueButton label="Add Your First Webhook" onClick={openCreate} />
        </div>
      ) : (
        <div className="space-y-3">
          {configs.map(c => (
            <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex h-2 w-2 rounded-full flex-shrink-0 ${c.isEnabled ? "bg-green-500" : "bg-slate-300"}`} />
                    <span className="font-medium text-slate-800 text-sm">{c.name || "Unnamed"}</span>
                    {!c.isEnabled && (
                      <span className="text-xs text-slate-400 bg-slate-100 px-1.5 rounded">Disabled</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 font-mono mb-2">{c.webhookUrl}</p>
                  <p className="text-xs text-slate-500">
                    Triggers: <span className="text-slate-700">{triggers(c)}</span>
                  </p>
                  {testResults[c.id] && (
                    <p className={`mt-2 text-xs ${testResults[c.id].success ? "text-green-700" : "text-red-600"}`}>
                      {testResults[c.id].success ? "✓ " : "✗ "}{testResults[c.id].message}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    className="text-xs text-blue-600 hover:underline"
                    onClick={() => test(c.id)}
                    disabled={testing === c.id}
                  >
                    {testing === c.id ? "Testing..." : "Test"}
                  </button>
                  <button
                    className="text-xs text-slate-500 hover:underline"
                    onClick={() => toggleEnabled(c)}
                  >
                    {c.isEnabled ? "Disable" : "Enable"}
                  </button>
                  <button
                    className="text-xs text-slate-500 hover:underline"
                    onClick={() => openEdit(c)}
                  >
                    Edit
                  </button>
                  <button
                    className="text-xs text-red-500 hover:underline"
                    onClick={() => deleteConfig(c.id)}
                    disabled={deleting === c.id}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Activity Log */}
      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="Slack" />
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-5">
              {editing ? "Edit Webhook" : "Add Slack Webhook"}
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Name</label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="e.g. Support Channel, #escalations"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Slack Incoming Webhook URL <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="https://hooks.slack.com/services/T.../B.../..."
                  value={form.webhookUrl}
                  onChange={e => setForm(f => ({ ...f, webhookUrl: e.target.value }))}
                />
                <p className="text-xs text-slate-400 mt-1">
                  From api.slack.com/apps → Your App → Incoming Webhooks → Add New Webhook
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">Notify on</label>
                <div className="space-y-2">
                  {[
                    { key: "notifyOnResolve",   label: "Conversation resolved",       desc: "When an agent marks a conversation resolved" },
                    { key: "notifyOnNewChat",   label: "New inbound conversation",    desc: "When a new chat arrives in the queue" },
                    { key: "notifyOnMissed",    label: "Missed conversation",         desc: "When no agent accepts within the timeout" },
                    { key: "notifyOnSlaBreach", label: "SLA breach",                  desc: "When a conversation exceeds its SLA threshold" }
                  ].map(t => (
                    <label key={t.key} className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        checked={form[t.key as keyof FormState] as boolean}
                        onChange={e => setForm(f => ({ ...f, [t.key]: e.target.checked }))}
                      />
                      <div>
                        <p className="text-sm text-slate-700 group-hover:text-slate-900">{t.label}</p>
                        <p className="text-xs text-slate-400">{t.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {saveError && (
                <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{saveError}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <SecondaryBlueButton label="Cancel" onClick={() => setShowModal(false)} disabled={saving} />
              <PrimaryBlueButton label={saving ? "Saving..." : "Save"} onClick={save} disabled={saving} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SetupInstructions() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-5 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>How to create a Slack incoming webhook</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-slate-100 text-sm text-slate-600 space-y-3">
          <ol className="mt-4 space-y-3">
            {[
              { n: "1", text: "Go to api.slack.com/apps and sign in to your Slack workspace." },
              { n: "2", text: "Click Create New App → From scratch. Give it a name like \"Velaro\" and pick your workspace." },
              { n: "3", text: "In the left sidebar, click Incoming Webhooks and toggle Activate Incoming Webhooks to On." },
              { n: "4", text: "Click Add New Webhook to Workspace and pick the channel where notifications should appear." },
              { n: "5", text: "Copy the Webhook URL shown — it starts with https://hooks.slack.com/services/..." },
              { n: "6", text: "Paste it here, name it, choose your triggers, Save, then click Test to verify." }
            ].map(s => (
              <li key={s.n} className="flex gap-3">
                <span className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-[#4A154B] text-white text-xs font-bold mt-0.5">{s.n}</span>
                <p className="text-xs text-slate-600 leading-relaxed">{s.text}</p>
              </li>
            ))}
          </ol>
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 mt-3">
            <strong>Tip:</strong> You can add multiple webhooks pointing to different Slack channels — for example, one for #support-alerts and another for #escalations.
            Each webhook can have different trigger settings.
          </div>
        </div>
      )}
    </div>
  );
}
